// client/src/components/styles/Styler.js

import React, { useState, useEffect } from "react";
import useStyles from "./useStyles";

async function uploadImage(file, mapId, fieldName) {
  const formData = new FormData();
  formData.append(fieldName, file);
  const res = await fetch(`/api/map-styles/${mapId}/styles/upload-images`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok || !data[fieldName + "_url"])
    throw new Error(data.error || "Failed to upload image");
  return data[fieldName + "_url"];
}

const fontFamilies = [
  "inherit",
  "Arial",
  "Tahoma",
  "Verdana",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Comic Sans MS",
  "Roboto",
  "Montserrat",
  "Lato",
  "Source Sans Pro",
];

const colourInputStyle = {
  width: 40,
  height: 32,
  border: "none",
  marginLeft: 12,
  marginRight: 4,
  verticalAlign: "middle",
  cursor: "pointer",
};

const defaultPreviewBg = "#1a1c1f";

export default function Styler({ mapId, user, open, onClose }) {
  const {
    styles,
    defaultStyles,
    loading,
    saving,
    error,
    saveStyles,
    resetStyles,
    setDefault,
    refetch,
    setStyles,
  } = useStyles(mapId, user);

  const [local, setLocal] = useState(null);
  const [restoringDefault, setRestoringDefault] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (open && styles) setLocal({ ...styles });
    if (!open) {
      setRestoringDefault(false);
      setUploading({});
    }
  }, [open, styles]);

  useEffect(() => {
    if (restoringDefault && defaultStyles) {
      setLocal({ ...defaultStyles });
    }
  }, [restoringDefault, defaultStyles]);

  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose && onClose();
    }
    if (open) window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const handleField = (field, value) => {
    setLocal((cur) => ({ ...cur, [field]: value }));
  };

  const handleImageUpload = async (field, file) => {
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const url = await uploadImage(
        file,
        mapId,
        field === "title_image_url" ? "title_image" : "watermark"
      );
      setLocal((cur) => ({ ...cur, [field]: url }));
    } catch (e) {
      alert(e.message || "Image upload failed");
    }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleImageDelete = (field) => {
    setLocal((cur) => ({ ...cur, [field]: "" }));
  };

  const handleSave = () => {
    const {
      title_text,
      title_image_url,
      font_family,
      font_colour,
      sidebar_bg_colour,
      main_bg_colour,
      section_name_pois,
      section_name_zones,
      section_name_region_maps,
      overlay_menu_title,
      overlay_menu_size,
      overlay_menu_base_label,
    } = local || {};

    saveStyles({
      title_text,
      title_image_url,
      font_family,
      font_colour,
      sidebar_bg_colour,
      main_bg_colour,
      section_name_pois,
      section_name_zones,
      section_name_region_maps,
      overlay_menu_title,
      overlay_menu_size,
      overlay_menu_base_label,
    }).then(() => {
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("pa-map-styles-updated", {
              detail: { mapId: mapId },
            })
          );
        }
      } catch {}

      refetch();
      onClose && onClose();
    });
  };

  const handleReset = () => {
    setRestoringDefault(false);
    setLocal(styles ? { ...styles } : {});
  };

  const handleRestoreDefault = () => {
    setRestoringDefault(true);
    if (defaultStyles) setLocal({ ...defaultStyles });
  };

  const handleSetDefault = () => {
    if (window.confirm("Set current style as the global default for all maps?")) {
      setDefault();
    }
  };

  const previewFont = local?.font_family || "inherit";
  const previewFontColour = local?.font_colour || "#fff";
  const previewSidebarBg = local?.sidebar_bg_colour || "#202124";
  const previewMainBg = local?.main_bg_colour || defaultPreviewBg;

  const previewTitleImage = local?.title_image_url;
  const previewTitleText = local?.title_text || "Map Title";

  const overlayMenuTitlePreview =
    (local?.overlay_menu_title || "").trim() || "Floors";
  const overlayMenuSizePreview = (local?.overlay_menu_size || "medium").toLowerCase();

  const overlayMenuBaseLabelPreview =
    (local?.overlay_menu_base_label || "").trim() || "Ground floor (no overlay)";

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(20,20,28,0.88)",
        zIndex: 3000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          background: "#222",
          color: "#fff",
          borderRadius: 18,
          minWidth: 690,
          maxWidth: "94vw",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 6px 40px #000b",
          padding: 0,
          display: "flex",
          flexDirection: "row",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          style={{
            minWidth: 330,
            padding: "28px 30px 28px 26px",
            borderRight: "2.5px solid #393c46",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            fontSize: 16,
            flex: "0 0 370px",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 22,
              marginBottom: 10,
              letterSpacing: "1.5px",
            }}
          >
            Edit Map Styles
          </div>

          <label
            style={{
              fontWeight: 600,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            Title Text
            <input
              type="text"
              value={local?.title_text || ""}
              onChange={(e) => handleField("title_text", e.target.value)}
              disabled={!!local?.title_image_url}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#222",
                color: "#fff",
                opacity: local?.title_image_url ? 0.6 : 1,
              }}
              maxLength={50}
              placeholder="Enter map title"
            />
          </label>

          <label style={{ fontWeight: 600 }}>
            Title Image (overrides text)
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ width: 140, fontSize: 13, background: "#111" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0])
                      handleImageUpload("title_image_url", e.target.files[0]);
                  }}
                  disabled={uploading.title_image_url}
                />
                {local?.title_image_url && (
                  <>
                    <img
                      src={local.title_image_url}
                      alt="title"
                      style={{
                        height: 34,
                        maxWidth: 110,
                        objectFit: "contain",
                        borderRadius: 5,
                        background: "#18181e",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleImageDelete("title_image_url")}
                      style={{
                        fontSize: 13,
                        background: "#222",
                        color: "#feb",
                        border: "1px solid #a78",
                        borderRadius: 5,
                        marginLeft: 4,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#ccc", fontStyle: "italic" }}>
                Recommended image ratio: ~4:1 (width:height) for best display
              </div>
            </div>
          </label>

          <label style={{ fontWeight: 600 }}>
            Font Family
            <select
              value={local?.font_family || "inherit"}
              onChange={(e) => handleField("font_family", e.target.value)}
              style={{
                fontSize: 15,
                padding: "6px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#23232b",
                color: "#fff",
                marginLeft: 10,
                marginTop: 2,
              }}
            >
              {fontFamilies.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            Font Colour
            <input
              type="color"
              value={local?.font_colour || "#ffffff"}
              onChange={(e) => handleField("font_colour", e.target.value)}
              style={colourInputStyle}
            />
            <span style={{ fontSize: 13, color: "#aaa", marginLeft: 7 }}>
              {local?.font_colour || "#fff"}
            </span>
          </label>

          <label style={{ fontWeight: 600, marginTop: 4 }}>
            Sidebar Background Colour
            <input
              type="color"
              value={local?.sidebar_bg_colour || "#202124"}
              onChange={(e) => handleField("sidebar_bg_colour", e.target.value)}
              style={colourInputStyle}
            />
            <span style={{ fontSize: 13, color: "#aaa", marginLeft: 7 }}>
              {local?.sidebar_bg_colour || "#202124"}
            </span>
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            Main Background Colour
            <input
              type="color"
              value={local?.main_bg_colour || defaultPreviewBg}
              onChange={(e) => handleField("main_bg_colour", e.target.value)}
              style={colourInputStyle}
            />
            <span style={{ fontSize: 13, color: "#aaa", marginLeft: 7 }}>
              {local?.main_bg_colour || defaultPreviewBg}
            </span>
          </label>

          <label style={{ fontWeight: 600, marginTop: 8 }}>
            Overlay Menu Title
            <input
              type="text"
              value={local?.overlay_menu_title || ""}
              onChange={(e) => handleField("overlay_menu_title", e.target.value)}
              maxLength={24}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#18181d",
                color: "#fff",
              }}
              placeholder="Floors"
            />
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            Overlay Menu Size
            <select
              value={(local?.overlay_menu_size || "medium").toLowerCase()}
              onChange={(e) => handleField("overlay_menu_size", e.target.value)}
              style={{
                fontSize: 15,
                padding: "6px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#23232b",
                color: "#fff",
                marginTop: 2,
              }}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="hidden">Hidden</option>
            </select>
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            Base Layer Label (overlay menu)
            <input
              type="text"
              value={local?.overlay_menu_base_label || ""}
              onChange={(e) =>
                handleField("overlay_menu_base_label", e.target.value)
              }
              maxLength={40}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#18181d",
                color: "#fff",
              }}
              placeholder="Ground floor (no overlay)"
            />
          </label>

          <label style={{ fontWeight: 600, marginTop: 8 }}>
            "POIs" Section Name
            <input
              type="text"
              value={local?.section_name_pois || "POIs"}
              onChange={(e) => handleField("section_name_pois", e.target.value)}
              maxLength={20}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#18181d",
                color: "#fff",
              }}
            />
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            "Zones" Section Name
            <input
              type="text"
              value={local?.section_name_zones || "Zones"}
              onChange={(e) => handleField("section_name_zones", e.target.value)}
              maxLength={20}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#18181d",
                color: "#fff",
              }}
            />
          </label>

          <label style={{ fontWeight: 600, marginTop: 0 }}>
            "Region Maps" Section Name
            <input
              type="text"
              value={local?.section_name_region_maps || "Region Maps"}
              onChange={(e) =>
                handleField("section_name_region_maps", e.target.value)
              }
              maxLength={20}
              style={{
                fontSize: 15,
                padding: "5px 9px",
                borderRadius: 6,
                border: "1px solid #444",
                background: "#18181d",
                color: "#fff",
              }}
            />
          </label>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              type="submit"
              disabled={saving || loading}
              style={{
                padding: "10px 32px",
                background: "#38d85c",
                color: "#112",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 17,
                boxShadow: "0 2px 9px #22ff9431",
                cursor: "pointer",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              disabled={saving || loading}
              onClick={handleReset}
              style={{
                padding: "10px 18px",
                background: "#23242c",
                color: "#eee",
                border: "1.5px solid #555",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 15,
                marginLeft: 3,
                cursor: "pointer",
              }}
            >
              Reset
            </button>
            <button
              type="button"
              disabled={saving || loading || !defaultStyles}
              onClick={handleRestoreDefault}
              style={{
                padding: "10px 18px",
                background: "#23242c",
                color: "#ffe089",
                border: "1.5px solid #886",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 15,
                marginLeft: 3,
                cursor: "pointer",
              }}
            >
              Restore Default
            </button>
          </div>

          {user && (user.role === "founder" || user.role === "admin") && (
            <button
              type="button"
              onClick={handleSetDefault}
              disabled={saving || loading}
              style={{
                marginTop: 7,
                padding: "9px 18px",
                background: "#2037a9",
                color: "#fff",
                border: "1.5px solid #77f",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 15,
                cursor: "pointer",
              }}
            >
              Set As Default For All Maps
            </button>
          )}

          {error && (
            <div style={{ color: "#ff6565", marginTop: 5, fontWeight: 600 }}>
              {error}
            </div>
          )}
        </form>

        <div
          style={{
            flex: 1,
            minWidth: 320,
            padding: "22px 30px 16px 26px",
            background: previewMainBg,
            color: previewFontColour,
            fontFamily: previewFont,
            borderRadius: "0 18px 18px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: 16,
              marginTop: 2,
            }}
          >
            {previewTitleImage ? (
              <img
                src={previewTitleImage}
                alt="title"
                style={{
                  height: 48,
                  objectFit: "contain",
                  marginBottom: 2,
                  marginTop: 3,
                  borderRadius: 6,
                  background: "#191a1d",
                }}
              />
            ) : (
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 32,
                  letterSpacing: "2px",
                  color: previewFontColour,
                  fontFamily: previewFont,
                  textShadow: "0 2px 8px #0008",
                }}
              >
                {previewTitleText}
              </span>
            )}
          </div>

          <div
            style={{
              width: "98%",
              background: previewSidebarBg,
              borderRadius: 12,
              minHeight: 172,
              margin: "0 auto",
              padding: "22px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 18,
              color: previewFontColour,
              fontFamily: previewFont,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {local?.section_name_pois || "POIs"}
            </div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {local?.section_name_zones || "Zones"}
            </div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {local?.section_name_region_maps || "Region Maps"}
            </div>

            <div style={{ marginTop: 12, opacity: 0.9, fontSize: 13 }}>
              Overlay Menu:{" "}
              <span style={{ fontWeight: 700 }}>{overlayMenuTitlePreview}</span>{" "}
              ({overlayMenuSizePreview})
            </div>

            <div style={{ opacity: 0.9, fontSize: 13 }}>
              Base Layer Label:{" "}
              <span style={{ fontWeight: 700 }}>
                {overlayMenuBaseLabelPreview}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 18,
            right: 16,
            background: "transparent",
            color: "#eee",
            border: "none",
            fontSize: 32,
            fontWeight: 700,
            cursor: "pointer",
            zIndex: 20,
            opacity: 0.84,
          }}
          title="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
}
